import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UserPlus,
  Trash2,
  Search,
  Mail,
  Calendar,
  Shield,
  Edit,
  Table,
  Users,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/api/client";
import { toastHelpers } from "@/lib/toast-helpers";
import { UserForm } from "@/components/forms/UserForm";
import { AdminStaffTable } from "@/components/admin/AdminStaffTable";
import { PaginationControlsComponent } from "@/components/ui/pagination-controls";
import { usePagination } from "@/hooks/usePagination";
import { InlineLoading } from "@/components/ui/loading-spinner";
import type { User } from "@/types";
import { useRouter } from "node_modules/@tanstack/react-router/dist/esm/useRouter";
import { useNavigationRefresh } from "@/hooks/useNavigationRefresh";

type DisplayMode = "table" | "cards";

export function AdminStaffManagement() {
  const { t } = useTranslation();
  const router = useRouter();

  // Manual refresh functionality
  const { manualRefresh, isRefreshing } = useNavigationRefresh(["users"]);
  useEffect(() => {
    const decodedToken = apiClient.isAuthenticated();

    if (!decodedToken) {
      toastHelpers.sessionExpired();
      router.navigate({ to: "/login" });
    }
  }, []);

  const [displayMode, setDisplayMode] = useState<DisplayMode>("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const queryClient = useQueryClient();

  // Responsive breakpoint detection
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileSize = window.innerWidth < 768;
      setIsMobile(isMobileSize);
      // Auto switch to cards view on mobile
      if (isMobileSize && displayMode === "table") {
        setDisplayMode("cards");
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [displayMode]);

  // Pagination hook
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 10,
    total: 0,
  });

  // Debounce search term
  useEffect(() => {
    if (searchTerm !== debouncedSearch) {
      setIsSearching(true);
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      pagination.goToFirstPage();
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  // Fetch users with pagination
  const { data: usersData, isLoading } = useQuery({
    queryKey: ["users", pagination.page, pagination.pageSize, debouncedSearch],
    queryFn: () => apiClient.getUsers().then((res: any) => res.data),
  });

  // Show loading screen when backend is called (not cache fetch) or refreshing
  const isLoadingAny = isRefreshing || isLoading;

  // Extract data and pagination info
  const users = Array.isArray(usersData)
    ? usersData
    : (usersData as any)?.data || [];
  const paginationInfo = (usersData as any)?.pagination || { total: 0 };

  // Delete user mutation (keep existing functionality)
  const deleteUserMutation = useMutation({
    mutationFn: ({ id }: { id: string; username: string }) =>
      apiClient.deleteUser(id),
    onSuccess: (_, { username: deletedUsername }) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toastHelpers.userDeleted(deletedUsername);
    },
    onError: (error: any) => {
      toastHelpers.apiError("Delete user", error);
    },
  });

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleDeleteUser = (user: User) => {
    const displayName = `${user.name} with ${user.email}`;
    if (
      confirm(t("AdminStaffManagement.Confirm.DeleteUser", { displayName }))
    ) {
      deleteUserMutation.mutate({
        id: user.id.toString(),
        username: displayName,
      });
    }
  };

  // Data is already filtered on the server side
  const filteredUsers = users;

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800 hover:bg-red-200";
      case "manager":
        return "bg-purple-100 text-purple-800 hover:bg-purple-200";
      case "server":
        return "bg-blue-100 text-blue-800 hover:bg-blue-200";
      case "counter":
        return "bg-green-100 text-green-800 hover:bg-green-200";
      case "kitchen":
        return "bg-orange-100 text-orange-800 hover:bg-orange-200";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
    }
  };

  // Show form if creating or editing
  if (showCreateForm || editingUser) {
    return (
      <div className={isMobile ? "p-4" : "p-6"}>
        <UserForm
          user={editingUser || undefined}
          mode={editingUser ? "edit" : "create"}
          onSuccess={handleFormSuccess}
          onCancel={handleCancelForm}
        />
      </div>
    );
  }

  // Always show header and search; content area will show skeletons when loading

  return (
    <div className={isMobile ? "p-4 space-y-4" : "p-6 space-y-6"}>
      {/* Header */}
      <div
        className={isMobile ? "space-y-3" : "flex items-center justify-between"}
      >
        <div>
          <h2
            className={
              isMobile
                ? "text-2xl font-bold tracking-tight"
                : "text-3xl font-bold tracking-tight"
            }
          >
            {t("AdminStaffManagement.Heading")}
          </h2>
          <p
            className={
              isMobile
                ? "text-sm text-muted-foreground"
                : "text-muted-foreground"
            }
          >
            {isMobile
              ? t("AdminStaffManagement.SubheadingMobile")
              : t("AdminStaffManagement.Subheading")}
          </p>
        </div>
        {!isMobile && (
          <div className="flex items-center space-x-4">
            {/* Refresh Button */}
            <Button
              variant="outline"
              onClick={manualRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {isRefreshing
                ? t("AdminStaffManagement.Refreshing")
                : t("AdminStaffManagement.Refresh")}
            </Button>
            {/* View Toggle */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Button
                variant={displayMode === "table" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDisplayMode("table")}
                className="px-3"
              >
                <Table className="h-4 w-4 mr-1" />
                {t("AdminStaffManagement.ViewMode.Table")}
              </Button>
              <Button
                variant={displayMode === "cards" ? "default" : "ghost"}
                size="sm"
                onClick={() => setDisplayMode("cards")}
                className="px-3"
              >
                <Users className="h-4 w-4 mr-1" />
                {t("AdminStaffManagement.ViewMode.Cards")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Search and Add Staff */}
      <Card>
        <CardContent className={isMobile ? "pt-4 pb-4" : "pt-6"}>
          <div
            className={
              isMobile ? "space-y-3" : "flex items-center justify-between gap-4"
            }
          >
            <div className="relative flex-1">
              <Search
                className={
                  isMobile
                    ? "absolute left-2 top-2.5 text-muted-foreground h-3 w-3"
                    : "absolute left-2 top-2.5 text-muted-foreground h-4 w-4"
                }
              />
              <Input
                placeholder={
                  isMobile
                    ? t("AdminStaffManagement.SearchPlaceholderMobile")
                    : t("AdminStaffManagement.SearchPlaceholder")
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={isMobile ? "pl-8 text-sm" : "pl-8"}
              />
              {isSearching && (
                <div className="absolute right-2 top-2.5">
                  <InlineLoading size="sm" />
                </div>
              )}
            </div>

            <div
              className={
                isMobile
                  ? "flex flex-wrap gap-2"
                  : "flex items-center space-x-4"
              }
            >
              {/* Mobile Refresh Button */}
              {isMobile && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={manualRefresh}
                  disabled={isRefreshing}
                  className="flex items-center gap-1 px-2"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              )}
              {/* Mobile View Toggle */}
              {isMobile && (
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={displayMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setDisplayMode("table")}
                    className="px-2 text-xs h-7"
                  >
                    <Table className="h-3 w-3" />
                  </Button>
                  <Button
                    variant={displayMode === "cards" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setDisplayMode("cards")}
                    className="px-2 text-xs h-7"
                  >
                    <Users className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <Button
                onClick={() => setShowCreateForm(true)}
                className={isMobile ? "gap-1 text-sm flex-1" : "gap-2"}
                size={isMobile ? "sm" : "default"}
              >
                <UserPlus className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                {isMobile
                  ? t("AdminStaffManagement.Add")
                  : t("AdminStaffManagement.AddStaff")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 relative min-h-[400px]">
        {/* Enhanced skeleton during loading */}
        {isLoadingAny && (
          <div className="space-y-6 sm:space-y-8">
            {/* Search Bar Skeleton */}
            <div className="bg-white rounded-lg border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="bg-muted animate-pulse rounded-md h-10 flex-1" />
                <div className="bg-muted animate-pulse rounded-md h-10 w-24 sm:w-32" />
              </div>
            </div>

            {/* Staff Table/Cards Skeleton */}
            {displayMode === "table" ? (
              <div className="bg-white rounded-lg border overflow-x-auto">
                {/* Table header */}
                <div className="hidden sm:grid grid-cols-5 gap-4 p-4 bg-muted/30 border-b min-w-[600px]">
                  <div className="bg-muted animate-pulse rounded h-4" />
                  <div className="bg-muted animate-pulse rounded h-4" />
                  <div className="bg-muted animate-pulse rounded h-4" />
                  <div className="bg-muted animate-pulse rounded h-4" />
                  <div className="bg-muted animate-pulse rounded h-4" />
                </div>

                {/* Table rows */}
                {[...Array(isMobile ? 4 : 6)].map((_, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:gap-4 p-3 sm:p-4 border-b min-w-[600px]"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-muted animate-pulse rounded-full h-8 w-8 sm:h-10 sm:w-10" />
                      <div className="bg-muted animate-pulse rounded h-3 sm:h-4 w-20 sm:w-24" />
                    </div>
                    <div className="bg-muted animate-pulse rounded h-3 sm:h-4 w-24 sm:w-32" />
                    <div className="bg-muted animate-pulse rounded h-5 sm:h-6 w-16 sm:w-20" />
                    <div className="bg-muted animate-pulse rounded h-3 sm:h-4 w-20 sm:w-24" />
                    <div className="flex gap-2">
                      <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-14 sm:w-16" />
                      <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-14 sm:w-16" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {[...Array(isMobile ? 4 : 8)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg border p-4 sm:p-6 space-y-3 sm:space-y-4"
                  >
                    {/* User avatar and name */}
                    <div className="flex items-center space-x-3">
                      <div className="bg-muted animate-pulse rounded-full h-10 w-10 sm:h-12 sm:w-12" />
                      <div className="space-y-2">
                        <div className="bg-muted animate-pulse rounded h-3 sm:h-4 w-20 sm:w-24" />
                        <div className="bg-muted animate-pulse rounded h-2 sm:h-3 w-24 sm:w-32" />
                      </div>
                    </div>

                    {/* Role badge */}
                    <div className="bg-muted animate-pulse rounded-full h-5 sm:h-6 w-16 sm:w-20" />

                    {/* Contact info */}
                    <div className="space-y-2">
                      <div className="bg-muted animate-pulse rounded h-2 sm:h-3 w-full" />
                      <div className="bg-muted animate-pulse rounded h-2 sm:h-3 w-20 sm:w-28" />
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <div className="bg-muted animate-pulse rounded h-7 sm:h-8 flex-1" />
                      <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-8 sm:w-10" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Skeleton */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className="bg-muted animate-pulse rounded h-3 sm:h-4 w-24 sm:w-32" />
              <div className="flex gap-1 sm:gap-2">
                <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-7 sm:w-8" />
                <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-7 sm:w-8" />
                <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-7 sm:w-8" />
                <div className="bg-muted animate-pulse rounded h-7 sm:h-8 w-7 sm:w-8" />
              </div>
            </div>
          </div>
        )}

        <div
          className={
            isLoadingAny && !isRefreshing
              ? "pointer-events-none opacity-50"
              : isLoading || isRefreshing
                ? "hidden"
                : ""
          }
        >
          {/* Staff List */}
          <div className="space-y-4">
            {displayMode === "table" ? (
              <AdminStaffTable
                data={filteredUsers}
                onEdit={setEditingUser}
                onDelete={handleDeleteUser}
                isLoading={isLoading}
              />
            ) : filteredUsers.length === 0 ? (
              <Card>
                <CardContent className={isMobile ? "pt-4" : "pt-6"}>
                  <div className="text-center py-6 sm:py-8">
                    <UserPlus
                      className={
                        isMobile
                          ? "mx-auto text-gray-400 h-8 w-8"
                          : "mx-auto text-gray-400 h-12 w-12"
                      }
                    />
                    <h3 className="mt-2 font-medium text-gray-900 text-sm">
                      {t("AdminStaffManagement.EmptyState.Title")}
                    </h3>
                    <p
                      className={
                        isMobile
                          ? "mt-1 text-gray-500 text-xs"
                          : "mt-1 text-gray-500 text-sm"
                      }
                    >
                      {searchTerm
                        ? t("AdminStaffManagement.EmptyState.NoMatch")
                        : t("AdminStaffManagement.EmptyState.GetStarted")}
                    </p>
                    {!searchTerm && (
                      <div className="mt-4 sm:mt-6">
                        <Button
                          onClick={() => setShowCreateForm(true)}
                          className="gap-2"
                          size={isMobile ? "sm" : "default"}
                        >
                          <UserPlus
                            className={isMobile ? "h-3 w-3" : "h-4 w-4"}
                          />
                          {t("AdminStaffManagement.AddNewStaff")}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                {filteredUsers.map((user: User) => (
                  <Card
                    key={user.id}
                    className="hover:shadow-md transition-shadow w-full"
                  >
                    <CardContent className={isMobile ? "p-4" : "pt-6"}>
                      <div
                        className={
                          isMobile
                            ? "flex flex-col space-y-3"
                            : "flex items-center justify-between"
                        }
                      >
                        <div
                          className={
                            isMobile
                              ? "flex flex-col items-center space-y-2 w-full"
                              : "flex items-center space-x-4"
                          }
                        >
                          <div className="flex-shrink-0">
                            <div
                              className={
                                isMobile
                                  ? "rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center h-12 w-12"
                                  : "rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center h-12 w-12"
                              }
                            >
                              <span
                                className={
                                  isMobile
                                    ? "font-semibold text-white text-sm"
                                    : "font-semibold text-white text-sm"
                                }
                              >
                                {user.name[0]}
                              </span>
                            </div>
                          </div>
                          <div
                            className={
                              isMobile
                                ? "w-full text-center space-y-1"
                                : "min-w-0 flex-1"
                            }
                          >
                            <h3
                              className={
                                isMobile
                                  ? "font-semibold text-gray-900 text-base"
                                  : "font-medium text-gray-900"
                              }
                            >
                              {user.name}
                            </h3>
                            <div
                              className={
                                isMobile
                                  ? "flex items-center gap-1 mt-1 justify-center"
                                  : "flex items-center gap-2 mt-1"
                              }
                            >
                              <Mail
                                className={
                                  isMobile ? "h-3 w-3 flex-shrink-0" : "h-4 w-4"
                                }
                              />
                              <p
                                className={
                                  isMobile
                                    ? "text-gray-600 text-xs"
                                    : "text-gray-500 text-sm truncate"
                                }
                              >
                                {user.email}
                              </p>
                            </div>
                            {user.created_at && (
                              <div
                                className={
                                  isMobile
                                    ? "flex items-center gap-1 mt-1 justify-center"
                                    : "flex items-center gap-2 mt-1"
                                }
                              >
                                <Calendar
                                  className={
                                    isMobile
                                      ? "h-3 w-3 flex-shrink-0"
                                      : "h-4 w-4"
                                  }
                                />
                                <p
                                  className={
                                    isMobile
                                      ? "text-gray-600 text-xs"
                                      : "text-gray-500 text-sm"
                                  }
                                >
                                  {t("AdminStaffManagement.Joined")}{" "}
                                  {new Date(user.created_at).toLocaleDateString(
                                    undefined,
                                    isMobile
                                      ? {
                                          month: "short",
                                          day: "numeric",
                                          year: "2-digit",
                                        }
                                      : undefined
                                  )}
                                </p>
                              </div>
                            )}
                            <div
                              className={
                                isMobile
                                  ? "flex items-center gap-1 mt-1 justify-center"
                                  : "flex items-center gap-2 mt-2"
                              }
                            >
                              <Shield
                                className={
                                  isMobile ? "h-3 w-3 flex-shrink-0" : "h-4 w-4"
                                }
                              />
                              <Badge
                                className={
                                  isMobile
                                    ? `${getRoleBadgeColor(user.role)} text-xs px-2 py-0.5`
                                    : getRoleBadgeColor(user.role)
                                }
                              >
                                {user.role}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div
                          className={
                            isMobile
                              ? "flex gap-2 w-full pt-3 border-t mt-3"
                              : "flex flex-col space-y-2 ml-4"
                          }
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingUser(user)}
                            className={
                              isMobile ? "flex-1 gap-1 text-xs h-9" : "gap-1"
                            }
                          >
                            <Edit
                              className={isMobile ? "h-3 w-3" : "h-4 w-4"}
                            />
                            {t("AdminStaffManagement.Actions.Edit")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteUser(user)}
                            className={
                              isMobile
                                ? "flex-1 gap-1 text-xs h-9 text-red-600 hover:text-red-700 hover:border-red-300"
                                : "gap-1 text-red-600 hover:text-red-700 hover:border-red-300"
                            }
                          >
                            <Trash2
                              className={isMobile ? "h-3 w-3" : "h-4 w-4"}
                            />
                            {t("AdminStaffManagement.Actions.Delete")}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {filteredUsers.length > 0 && (
              <div className={isMobile ? "mt-4 space-y-3" : "mt-6 space-y-4"}>
                {isLoadingAny && (
                  <div className="flex justify-center">
                    <InlineLoading
                      text={t("AdminStaffManagement.UpdatingStaff")}
                    />
                  </div>
                )}
                <PaginationControlsComponent
                  pagination={pagination}
                  total={paginationInfo.total || filteredUsers.length}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
